import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, MessageCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ImageLightbox } from './ImageLightbox';
import { WHATSAPP_NUMBER } from '../pages/ChristmasSessions';

export function ChristmasHero() {
  const { t, language } = useLanguage();
  const c = t.christmas;

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(c.whatsappMessage)}`;

  return (
    <section
      data-testid="christmas-hero"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-warm-50 pt-28 pb-16 lg:pt-32"
    >
      {/* Candle-light glows */}
      <div className="absolute top-1/4 right-[-10%] w-[40rem] h-[40rem] bg-accent/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[32rem] h-[32rem] bg-[#7B2D26]/[0.06] rounded-full blur-3xl" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="lg:col-span-6 xl:col-span-6"
          >
            <span className="inline-flex items-center gap-2 text-accent font-medium text-sm tracking-[0.2em] uppercase mb-6">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              {c.heroEyebrow}
            </span>

            <h1 className="font-heading text-5xl md:text-6xl 2xl:text-7xl font-semibold tracking-tight text-warm-900 leading-[1.05] mb-6">
              <span className="lg:whitespace-nowrap">{c.title}</span>
              <br />
              <span className="text-accent">{c.titleAccent}</span>
            </h1>

            <p className="text-xl md:text-2xl text-warm-700 italic font-light mb-8 max-w-lg text-balance">
              {c.tagline}
            </p>

            {/* Key facts — echoes the Instagram poster footer */}
            <dl className="grid grid-cols-2 max-w-lg mb-8 border-y border-warm-300/70">
              <div className="py-5 pr-4 sm:pr-6">
                <dt className="text-xs tracking-[0.2em] uppercase text-warm-600 mb-1.5">{c.startsLabel}</dt>
                <dd className="font-heading text-xl sm:text-2xl md:text-3xl text-warm-900 whitespace-nowrap">{c.startsDateShort}</dd>
              </div>
              <div className="py-5 pl-4 sm:pl-6 border-l border-warm-300/70">
                <dt className="text-xs tracking-[0.2em] uppercase text-warm-600 mb-1.5">{c.bookingLabel}</dt>
                <dd className="font-heading text-xl sm:text-2xl md:text-3xl text-warm-900 whitespace-nowrap">
                  <a href="tel:+351913446144" className="hover:text-accent transition-colors">913 446 144</a>
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-4">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="christmas-hero-whatsapp">
                <Button className="bg-accent hover:bg-accent-dark text-white h-14 px-9 rounded-full text-base transition-all duration-300 hover:shadow-lg">
                  <MessageCircle aria-hidden="true" />
                  {c.bookWhatsapp}
                </Button>
              </a>
              <Link to="/sessoes-de-natal" data-testid="christmas-hero-details">
                <Button
                  variant="outline"
                  className="border-2 border-accent bg-transparent text-accent hover:bg-accent hover:text-white h-14 px-9 rounded-full text-base transition-all duration-300"
                >
                  {c.heroDetails}
                  <ArrowRight aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Poster */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
            className="lg:col-span-6 relative mx-auto lg:mr-0 w-full"
            style={{ maxWidth: 'min(34rem, calc((100svh - 9rem) * 0.8))' }}
          >
            <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-3xl bg-warm-200" aria-hidden="true" />
            <ImageLightbox
              src="/images/natal-estudio.jpg"
              alt={language === 'pt'
                ? 'Sessões de Natal em estúdio — cenário com árvore iluminada, brinquedos de madeira e lanterna, na Atalaia, Montijo'
                : 'Christmas studio sessions — set with a glowing tree, wooden toys and a lantern, in Atalaia, Montijo'}
              width="1080"
              height="1350"
              className="w-full h-auto rounded-3xl shadow-2xl"
              openLabel={c.zoomImage}
              closeLabel={c.closeImage}
            />
            <div className="absolute -bottom-5 -left-5 md:-left-8 inline-flex items-center gap-2 bg-[#13241B] text-warm-50 rounded-full pl-4 pr-5 py-3 shadow-xl text-sm md:text-base font-medium">
              <Sparkles className="w-4 h-4 text-accent-light" aria-hidden="true" />
              {c.limitedBadge}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
